/**
 * StruggleHeatmap.js - Simple heatmap of student struggles
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL, apiFetch, isDemoMode } from '../config';

const StruggleHeatmap = ({ username, onClose, onPracticeRecommendation, curriculumContent }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grid, setGrid] = useState({});
  const [subtypes, setSubtypes] = useState({}); // { topic: { type: { subtype: count } } }
  const [maxCount, setMaxCount] = useState(1);
  const [selectedCell, setSelectedCell] = useState(null); // { topic, type, subtypeData }
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [recommendationCount, setRecommendationCount] = useState(3); // Number of recommendations (1-6, default 3)

  const types = ['SUCCESS', 'SYNTAX_ERROR', 'LOGIC_ERROR', 'RUNTIME_ERROR'];

  // Prevent background scrolling when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (isDemoMode()) {
        setLoading(false);
        setError('🔒 Learning Progress Map — Locked in Demo Mode\n\nWith a real account this heatmap shows your coding patterns across every Python topic:\n\n  🟢  Green cells = successful code runs on that topic\n  🟠  Orange/red cells = syntax, logic, or runtime errors\n  👆  Click any cell to get AI-powered study recommendations\n\nExample: many red cells on "Loops" → the AI suggests:\n  "Practice list iteration with 3 targeted exercises to close this gap."\n\n✨ Sign up to build your progress map!');
        return;
      }
      try {
        setLoading(true);
        const response = await apiFetch(
          `${API_BASE_URL}/api/logStudentStruggle?username=${encodeURIComponent(username)}&limit=600`
        );
        if (!response.ok) throw new Error('Failed to fetch');
        const result = await response.json();
        const logs = result.struggle_log || [];
        
        // Build grid: { topic: { type: count } }
        // Build subtypes: { topic: { type: { subtype: count } } }
        const g = {};
        const s = {};
        let max = 1;
        logs.forEach(log => {
          const topic = log.topic || 'other';
          const type = log.struggle_type || 'LOGIC_ERROR';
          const subtype = log.struggle_subtype || 'other';
          
          if (!g[topic]) g[topic] = { SUCCESS: 0, SYNTAX_ERROR: 0, LOGIC_ERROR: 0, RUNTIME_ERROR: 0 };
          g[topic][type]++;
          if (g[topic][type] > max) max = g[topic][type];
          
          // Track subtypes
          if (!s[topic]) s[topic] = {};
          if (!s[topic][type]) s[topic][type] = {};
          if (!s[topic][type][subtype]) s[topic][type][subtype] = 0;
          s[topic][type][subtype]++;
        });
        setGrid(g);
        setSubtypes(s);
        setMaxCount(max);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [username]);

  // Get study recommendations from LLM
  const getRecommendations = async (gridData, subtypesData) => {
    if (Object.keys(gridData).length === 0) return;
    
    setLoadingRecs(true);
    try {
      // Build a summary of struggle patterns for the LLM
      const summary = Object.entries(gridData)
        .map(([topic, counts]) => {
          const errors = counts.SYNTAX_ERROR + counts.LOGIC_ERROR + counts.RUNTIME_ERROR;
          const subtypeInfo = subtypesData[topic] || {};
          let subtypeDetails = [];
          Object.entries(subtypeInfo).forEach(([type, subs]) => {
            Object.entries(subs).forEach(([subtype, count]) => {
              if (count > 0 && subtype !== 'null') {
                subtypeDetails.push(`${subtype}(${count})`);
              }
            });
          });
          return `${topic}: ${counts.SUCCESS} success, ${errors} errors (${subtypeDetails.join(', ')})`;
        })
        .filter(line => line.includes('errors'))
        .join('\n');

      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `You are a Python tutor analyzing a student's struggle patterns. Based on the heatmap data showing their errors across different topics, provide exactly ${recommendationCount} specific, actionable study recommendations. Each recommendation should:
1. Target their weakest areas
2. Be specific about what to practice
3. Include a concrete example or exercise idea
${curriculumContent ? `
⚠️ CRITICAL SCOPE RULE: You MUST only recommend topics that are covered in the student's current curriculum below. Do NOT suggest any concept, syntax, or feature that does not appear in the curriculum. If a struggle topic is outside the curriculum scope, skip it and focus on in-scope struggles instead.

[STUDENT CURRICULUM - ALLOWED TOPICS ONLY]
${curriculumContent}
[END CURRICULUM]` : ''}

Return ONLY a JSON array of ${recommendationCount} objects with this format:
[
  { "topic": "topic name", "recommendation": "specific advice", "priority": "high/medium/low" },
  ...
]`
            },
            {
              role: 'user',
              content: `Here is the student's struggle heatmap data:\n${summary}\n\nProvide ${recommendationCount} study recommendations.`
            }
          ],
          temperature: 0,
          max_tokens: 800
        })
      });

      if (!response.ok) throw new Error('Failed to get recommendations');
      
      const data = await response.json();
      const content = data.choices[0]?.message?.content || '';
      
      // Parse JSON from response
      const jsonStr = content.replace(/```json\n?|\n?```/g, '').trim();
      const recs = JSON.parse(jsonStr);
      setRecommendations(recs);
    } catch (err) {
      console.error('Failed to get recommendations:', err);
      setRecommendations([]);
    } finally {
      setLoadingRecs(false);
    }
  };

  const handleCellClick = (topic, type) => {
    const count = grid[topic]?.[type] || 0;
    if (count === 0) return; // Don't show popup for empty cells
    
    const subtypeData = subtypes[topic]?.[type] || {};
    setSelectedCell({ topic, type, subtypeData });
  };

  const closePopup = () => setSelectedCell(null);

  const getColor = (count, type) => {
    if (count === 0) return '#21262d';
    // Calculate intensity 0.3 to 1.0 based on count vs max
    const ratio = count / maxCount;
    // Blend from dark to bright color
    const colors = {
      SUCCESS: { dark: [20, 60, 30], bright: [46, 160, 67] },
      SYNTAX_ERROR: { dark: [80, 60, 20], bright: [210, 153, 34] },
      LOGIC_ERROR: { dark: [90, 30, 30], bright: [248, 81, 73] },
      RUNTIME_ERROR: { dark: [60, 40, 90], bright: [163, 113, 247] }
    };
    const c = colors[type] || colors.LOGIC_ERROR;
    const r = Math.round(c.dark[0] + (c.bright[0] - c.dark[0]) * ratio);
    const g = Math.round(c.dark[1] + (c.bright[1] - c.dark[1]) * ratio);
    const b = Math.round(c.dark[2] + (c.bright[2] - c.dark[2]) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const formatTopic = (t) => t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Sort topics by total errors
  const topics = Object.keys(grid).sort((a, b) => {
    const aErr = grid[a].SYNTAX_ERROR + grid[a].LOGIC_ERROR + grid[a].RUNTIME_ERROR;
    const bErr = grid[b].SYNTAX_ERROR + grid[b].LOGIC_ERROR + grid[b].RUNTIME_ERROR;
    return bErr - aErr;
  });

  return (
    <>
    <style>{`
      @keyframes glow {
        from { text-shadow: 0 0 8px rgba(126, 232, 250, 0.5), 0 0 16px rgba(126, 232, 250, 0.2); }
        to { text-shadow: 0 0 12px rgba(126, 232, 250, 0.8), 0 0 24px rgba(126, 232, 250, 0.4), 0 0 32px rgba(126, 232, 250, 0.2); }
      }
    `}</style>
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      zIndex: 10000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#161b22',
        borderRadius: '12px',
        border: '1.5px solid rgba(255, 215, 102, 0.4)',
        width: '100%',
        maxWidth: '1600px',
        maxHeight: '95vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #30363d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: '#e6edf3', fontSize: '22px' }}>
            🗺️ Learning Progress Map
          </h2>
          <button onClick={onClose} style={{
            background: 'none',
            border: 'none',
            color: '#f0f6fc',
            fontSize: '24px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
          onMouseOver={(e) => e.target.style.color = '#f85149'}
          onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
          >✕</button>
        </div>

        {/* Content - Two Column Layout */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Side - Heatmap */}
          <div style={{ flex: '1 1 65%', padding: '20px', overflowY: 'auto', borderRight: '1px solid #30363d' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#8b949e', padding: '40px' }}>Loading...</div>
          ) : error ? (
            <div style={{ textAlign: 'left', color: error.startsWith('🔒') ? '#fffacd' : '#f85149', padding: '40px', whiteSpace: 'pre-wrap' }}>{error.startsWith('🔒') ? error : `Error: ${error}`}</div>
          ) : topics.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#8b949e', padding: '40px' }}>
              <p style={{ fontSize: '40px', margin: '0 0 12px 0' }}>📝</p>
              <p>No data yet!</p>
            </div>
          ) : (
            <>
              {/* Heatmap Container with border */}
              <div style={{
                border: '2px solid rgba(210, 153, 34, 0.3)',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '16px'
              }}>
              {/* Column Headers */}
              <div style={{ display: 'flex', marginBottom: '12px', paddingLeft: '200px' }}>
                {types.map(t => (
                  <div key={t} style={{
                    width: '140px',
                    textAlign: 'center',
                    fontSize: '14px',
                    color: t === 'SUCCESS' ? '#3fb950' : t === 'SYNTAX_ERROR' ? '#d29922' : t === 'LOGIC_ERROR' ? '#f85149' : '#a371f7',
                    fontWeight: '600'
                  }}>
                    {t === 'SUCCESS' ? 'Success' : t === 'SYNTAX_ERROR' ? 'Syntax Error' : t === 'LOGIC_ERROR' ? 'Logic Error' : 'Runtime Error'}
                  </div>
                ))}
              </div>

              {/* Heatmap Rows */}
              {topics.map(topic => (
                <div key={topic} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{
                    width: '200px',
                    fontSize: '15px',
                    color: '#c9d1d9',
                    paddingRight: '12px',
                    textAlign: 'right',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {formatTopic(topic)}
                  </div>
                  {types.map(type => {
                    const count = grid[topic]?.[type] || 0;
                    return (
                      <div
                        key={type}
                        onClick={() => handleCellClick(topic, type)}
                        style={{
                          width: '130px',
                          height: '48px',
                          margin: '0 5px',
                          borderRadius: '6px',
                          backgroundColor: getColor(count, type),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          fontWeight: count > 0 ? '600' : '400',
                          color: count > 0 ? '#fff' : '#484f58',
                          cursor: count > 0 ? 'pointer' : 'default',
                          transition: 'transform 0.1s, box-shadow 0.1s',
                          ':hover': count > 0 ? { transform: 'scale(1.05)' } : {}
                        }}
                        title={count > 0 ? `Click to see subtypes` : ''}
                        onMouseEnter={(e) => { if (count > 0) e.target.style.boxShadow = '0 0 8px rgba(255,255,255,0.3)'; }}
                        onMouseLeave={(e) => { e.target.style.boxShadow = 'none'; }}
                      >
                        {count > 0 ? count : ''}
                      </div>
                    );
                  })}
                </div>
              ))}
              </div> {/* End of heatmap border container */}

              {/* Help Text - Bottom Left */}
              <div style={{
                marginTop: '40px',
                padding: '14px',
                backgroundColor: '#21262d',
                borderRadius: '8px'
              }}>
                <div style={{ color: '#58a6ff', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                  📖 How to Read This Heatmap
                </div>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: '18px', 
                  color: '#8b949e', 
                  fontSize: '12px',
                  lineHeight: '1.8'
                }}>
                  <li><strong style={{ color: '#c9d1d9' }}>Rows</strong> = Python topics you've practiced</li>
                  <li><strong style={{ color: '#c9d1d9' }}>Columns</strong> = Result types (Success, Syntax/Logic/Runtime Errors)</li>
                  <li><strong style={{ color: '#c9d1d9' }}>Numbers</strong> = How many times each occurred</li>
                  <li><strong style={{ color: '#c9d1d9' }}>Colors</strong> = Brighter means more occurrences</li>
                  <li><strong style={{ color: '#58a6ff' }}>Click any cell</strong> to see specific error subtypes</li>
                  <li><strong style={{ color: '#00d4aa' }}>AI Recommendations</strong> on the right analyze your weakest areas</li>
                </ul>
              </div>

            </>
          )}
          </div>

          {/* Right Side - AI Recommendations */}
          <div style={{ flex: '1 1 35%', padding: '20px', overflowY: 'auto', backgroundColor: '#0d1117' }}>
            <h3 style={{ 
              margin: '0 0 16px 0', 
              color: '#00d4aa', 
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🤖 AI Study Recommendations
            </h3>
            
            {loadingRecs ? (
              <div style={{ 
                textAlign: 'center', 
                color: '#8b949e', 
                padding: '40px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid #30363d',
                  borderTopColor: '#00d4aa',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <style>{`
                  @keyframes spin {
                    to { transform: rotate(360deg); }
                  }
                `}</style>
                <div style={{ fontSize: '16px', color: '#00d4aa' }}>Analyzing your patterns...</div>
                <div style={{ fontSize: '13px' }}>Generating personalized recommendations</div>
              </div>
            ) : recommendations.length === 0 ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                padding: '40px 20px',
                textAlign: 'center'
              }}>
                <button
                  onClick={() => getRecommendations(grid, subtypes)}
                  style={{
                    backgroundColor: '#2ee8be',
                    color: '#0a0e1a',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '20px 30px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background-color 0.2s, transform 0.1s',
                    boxShadow: '0 4px 12px rgba(46, 232, 190, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#5af0d0';
                    e.target.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#2ee8be';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <span style={{ fontSize: '28px' }}>🔍</span>
                  <span>Click to Analyze Your Patterns</span>
                  <span style={{ fontSize: '12px', fontWeight: '400', opacity: 0.9 }}>
                    Generate personalized recommendations
                  </span>
                </button>
                <div style={{ 
                  marginTop: '20px', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <label style={{ color: '#c9d1d9', fontSize: '14px' }}>
                    Number of recommendations: <strong style={{ color: '#00b892' }}>{recommendationCount}</strong>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="6"
                    value={recommendationCount}
                    onChange={(e) => setRecommendationCount(parseInt(e.target.value))}
                    style={{
                      width: '150px',
                      accentColor: '#00b892',
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '150px', fontSize: '12px', color: '#c9d1d9' }}>
                    <span>1</span>
                    <span>3</span>
                    <span>6</span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <p style={{
                  margin: '0 0 8px 0',
                  color: '#7ee8fa',
                  fontSize: '13px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textShadow: '0 0 8px rgba(126, 232, 250, 0.6), 0 0 16px rgba(126, 232, 250, 0.3)',
                  animation: 'glow 2s ease-in-out infinite alternate'
                }}>
                  ✨ Click any recommendation below to start practicing with the tutor
                </p>
                {recommendations.map((rec, idx) => (
                  <div 
                    key={idx}
                    onClick={() => {
                      if (onPracticeRecommendation) {
                        onPracticeRecommendation(rec.recommendation, rec.topic);
                      }
                    }}
                    style={{
                      backgroundColor: '#161b22',
                      borderRadius: '8px',
                      padding: '14px',
                      borderLeft: `4px solid ${rec.priority === 'high' ? '#f85149' : rec.priority === 'medium' ? '#d29922' : '#3fb950'}`,
                      cursor: onPracticeRecommendation ? 'pointer' : 'default',
                      transition: 'background-color 0.2s, transform 0.1s'
                    }}
                    onMouseEnter={(e) => {
                      if (onPracticeRecommendation) {
                        e.currentTarget.style.backgroundColor = '#1f2937';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (onPracticeRecommendation) {
                        e.currentTarget.style.backgroundColor = '#161b22';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{ 
                        color: '#e6edf3', 
                        fontSize: '14px', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {idx + 1}. {formatTopic(rec.topic)}
                        {onPracticeRecommendation && (
                          <span style={{ 
                            fontSize: '11px', 
                            color: '#58a6ff',
                            fontWeight: '400'
                          }}>▶ Click to practice</span>
                        )}
                      </span>
                      <span style={{
                        fontSize: '10px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: rec.priority === 'high' ? '#f8514933' : rec.priority === 'medium' ? '#d2992233' : '#3fb95033',
                        color: rec.priority === 'high' ? '#f85149' : rec.priority === 'medium' ? '#d29922' : '#3fb950',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {rec.priority}
                      </span>
                    </div>
                    <p style={{ 
                      margin: 0, 
                      color: '#f5e07b', 
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}>
                      {rec.recommendation}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subtype Popup */}
      {selectedCell && (
        <div 
          onClick={closePopup}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 10001,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#21262d',
              borderRadius: '12px',
              border: '2px solid rgba(210, 153, 34, 0.5)',
              padding: '20px',
              minWidth: '300px',
              maxWidth: '450px',
              maxHeight: '400px',
              overflow: 'auto'
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px',
              borderBottom: '1px solid #30363d',
              paddingBottom: '12px'
            }}>
              <div>
                <div style={{ color: '#e6edf3', fontSize: '16px', fontWeight: '600' }}>
                  {formatTopic(selectedCell.topic)}
                </div>
                <div style={{ 
                  color: selectedCell.type === 'SUCCESS' ? '#3fb950' : 
                         selectedCell.type === 'SYNTAX_ERROR' ? '#d29922' : 
                         selectedCell.type === 'LOGIC_ERROR' ? '#f85149' : '#a371f7',
                  fontSize: '13px',
                  marginTop: '4px'
                }}>
                  {selectedCell.type === 'SUCCESS' ? 'Success' : 
                   selectedCell.type === 'SYNTAX_ERROR' ? 'Syntax Error' : 
                   selectedCell.type === 'LOGIC_ERROR' ? 'Logic Error' : 'Runtime Error'}
                </div>
              </div>
              <button 
                onClick={closePopup}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#f0f6fc',
                  fontSize: '22px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
                onMouseOver={(e) => e.target.style.color = '#f85149'}
                onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
              >✕</button>
            </div>
            
            <div style={{ color: '#8b949e', fontSize: '12px', marginBottom: '10px' }}>
              Subtypes breakdown:
            </div>
            
            {Object.entries(selectedCell.subtypeData)
              .sort((a, b) => b[1] - a[1])
              .map(([subtype, count]) => (
                <div 
                  key={subtype}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    backgroundColor: '#161b22',
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }}
                >
                  <span style={{ color: '#c9d1d9', fontSize: '14px' }}>
                    {formatTopic(subtype)}
                  </span>
                  <span style={{ 
                    color: '#e6edf3', 
                    fontSize: '16px', 
                    fontWeight: '600',
                    backgroundColor: '#30363d',
                    padding: '2px 10px',
                    borderRadius: '4px'
                  }}>
                    {count}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default StruggleHeatmap;
